import * as React from 'react';
import { InclinationMidrangeChart, PVAContainer, LoadingIndicator, PVAPage, PVATeamSelect, LoadingIndicatorMini, GroupChart } from '../../../components';
import { ReportType, MidRangeType } from '../../../enums';
import { ReportDataService, CharacteristicDataService, CharacteristicGroupDataService } from '../../../services';
import { User, CharacteristicGroup, Characteristic, Team, CharacteristicGroupCharacteristic, CharacteristicGroupSubGroup, CharacteristicGroupSubGroupCharacteristic, UserPermissions } from '../../../models';
import { BaseRouteProps } from '../../../interfaces';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import './TeamReport.css';


interface TeamReportState {
    orgId: number;
    surveyVersionId: number;
    selectedUserData: any;
    users: User[];
    reportType: ReportType;
    characteristicGroups: CharacteristicGroup[];
    characteristics: Characteristic[];
    loading: boolean;
    teamId: number | undefined;
    miniLoad: boolean;
    open: boolean;
}

interface TeamReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class TeamReport extends React.Component<TeamReportProps, TeamReportState> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private stdDev: number = 1.25;

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    constructor(props: TeamReportProps) {
        super(props);

        this.state = {
            orgId: 0,
            surveyVersionId: 0,
            selectedUserData: undefined,
            users: [],
            characteristicGroups: [],
            characteristics: [],
            reportType: ReportType.Standard,
            loading: false,
            teamId: -1,
            miniLoad: false,
            open: false
        };
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get team() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team;
        }
        return null;
    }

    @computed get teamId() {
        return this.props.FilterStore && this.props.FilterStore.teamId;
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }
    
    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get teams() {
        if (this.props.FilterStore && this.props.FilterStore.teams) {
            return this.props.FilterStore.teams;
        }
        return [];
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    public update() {

        var updateSurveyVersionID = false;
        if (this.userRole !== Roles.User) {
            if (this.surveyVersion && this.surveyVersion.id !== this.state.surveyVersionId) {
                this.setState({
                    surveyVersionId: this.surveyVersion.id
                });
                updateSurveyVersionID = true;

                this.characteristicGroupDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristicGroups: CharacteristicGroup[]) => {
                    this.setState({
                        characteristicGroups: characteristicGroups
                    });
                });

                this.characteristicDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristics: Characteristic[]) => {
                    this.setState({
                        characteristics: characteristics
                    });
                });

            }

            if (this.surveyVersion && (this.teamId !== this.state.teamId || updateSurveyVersionID)) {
                this.setState({
                    teamId: this.teamId
                });

                const surveyVersionId = this.surveyVersion.id;

                if (this.team) {
                    this.setState({
                        miniLoad: true
                    });

                    this.reportDataService.getTeamReportData(this.team, surveyVersionId)
                        .then((data: any) => {
                            this.setState(
                                {
                                    selectedUserData: data.entries,
                                    miniLoad: false
                                },
                                this.forceUpdate);
                        })
                        .catch(() => {
                            this.props.notificationService.addError('Error retrieving team report data.');
                            this.setState({miniLoad: false});
                        });
                } else if (this.organization && this.teamId === -100) {
                    this.setState({
                        miniLoad: true
                    });

                    this.reportDataService.getTeamOrganizationReportData(this.organization, surveyVersionId).then((data: any) => {
                        this.setState(
                            {
                                selectedUserData: data.entries,
                                miniLoad: false
                            },
                            this.forceUpdate);
                    })
                    .catch(() => {
                        this.props.notificationService.addError('Error retrieving team report data.');
                        this.setState({miniLoad: false});
                    });
                } else if (this.organization && this.teamId === -200) {
                    this.setState({
                        miniLoad: true
                    });

                    this.reportDataService.getTeamCandidateReportData(this.organization, surveyVersionId).then((data: any) => {
                        this.setState(
                            {
                                selectedUserData: data.entries,
                                miniLoad: false
                            },
                            this.forceUpdate);
                    })
                    .catch(() => {
                        this.props.notificationService.addError('Error retrieving team report data.');
                        this.setState({miniLoad: false});
                    });
                } else if (this.organization && this.teamId === -300) {
                    this.setState({
                        miniLoad: true
                    });

                    this.reportDataService.getTeamFormerEmployeeReportData(this.organization, surveyVersionId).then((data: any) => {
                        this.setState(
                            {
                                selectedUserData: data.entries,
                                miniLoad: false
                            },
                            this.forceUpdate);
                    })
                    .catch(() => {
                        this.props.notificationService.addError('Error retrieving team report data.');
                        this.setState({miniLoad: false});
                    });
                } 

            }
        }
    }

    componentDidMount() {
        this.update();
    }

    public componentWillReact() {
        this.update();
    }


    teamChange(e: any) {
        if (this.props.FilterStore) {
            var id = Number(e.target.value);
            var team = this.teams.find((t: Team) => {
                return t.id === id;
            });
            if (team) {
                this.props.FilterStore.setTeam(team);
            } else {
                // -100, -200, -300, etc
                this.props.FilterStore.setTeamID(id);
            }
        }
    }

    private ActionsButton() {
        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-header">Actions</li>
                    <li className="options-entry">      
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Standard Deviation</span>
                        </div>              
                        <select className="form-control" onChange={(e) => { this.stdDev = parseFloat(e.target.value); this.setState({ open: !this.state.open}, this.forceUpdate); }}>
                            <option selected={this.stdDev === 0.5} value={0.5}>STD DEV 0.5</option>
                            <option selected={this.stdDev === 0.75} value={0.75}>STD DEV 0.75</option>
                            <option selected={this.stdDev === 1.0} value={1.0}>STD DEV 1</option>
                            <option selected={this.stdDev === 1.25} value={1.25}>STD DEV 1.25</option>
                            <option selected={this.stdDev === 1.5} value={1.5}>STD DEV 1.5</option>
                        </select>
                    </li>          
                </ul>
        </div>);
    }

    public render() {
        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];
        
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllUsers) ) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllCandidates) ) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllFormerEmployees) ) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        return (
            <PVAPage>

                {this.organization && <div id={`organization-${this.organization.id}`} />}
                {this.survey && <div id={`survey-${this.survey.id}`} />}
                {this.surveyVersion && <div id={`surveyVersion-${this.surveyVersion.id}`} />}
                {this.surveyReportType && <div id={`surveyReportType-${this.surveyReportType.id}`} />}

                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <span>PVA Team Report<span className="trademark">TM</span></span>
                            <span className="light"> for </span>
                            <PVATeamSelect defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} />   
                            {this.state.miniLoad && <LoadingIndicatorMini />} 
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    <PVAPage.Header.Buttons>
                        {this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin]) && this.ActionsButton()}
                    </PVAPage.Header.Buttons>
                </PVAPage.Header>

                <PVAPage.Body>
                    {this.state.loading && <LoadingIndicator />}
                    <div className="group-charts">
                        {!this.state.loading && this.state.characteristicGroups.filter((group: CharacteristicGroup) => {
                                if (group.characteristicGroupDisplayType !== 'normal') {
                                    return false;
                                }
                                if (!group.characteristicGroupCharacteristics || group.characteristicGroupCharacteristics.length === 0) {
                                    return false;
                                }
                                if (group.secondaryReportID) {
                                    return false;
                                }
                                if (!this.surveyReportType) {
                                    return true;
                                }
                                return group.surveyReportTypeID === this.surveyReportType.id;
                            }).map((group: CharacteristicGroup, groupInd: number) => {
                                const groupData = this.transformToGroupData(group);
                                return <GroupChart
                                    key={`group-${groupInd}`}
                                    index={groupInd}
                                    groupData={groupData}
                                    teamID={this.teamId || 0}
                                    reportType={ReportType.Midrange}
                                    midRangeType={MidRangeType.Team}
                                    offset={0}
                                    headerOffset={true}
                                    allowToggle={this.props.UserStore!.checkUserPermission(UserPermissions.ScoreBarColorChange)}
                                />;
                        })}
                    </div>
                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }

    private minMaxFloor(val: number) {
        if (val < 0) {
            return 0;
        }
        if (val > 100) {
            return 100;
        }
        return Math.round(val);
    }

    private sortOrder(a: any, b: any) {
        if (a.sort > b.sort) {
            return 1;
        }
        if (a.sort < b.sort) {
            return -1;
        }
        return 0;
    }

    private transformToGroupData(group: CharacteristicGroup): any {
        const aboutText = group.characteristicGroupTexts ? group.characteristicGroupTexts.find(x => x.textType === 'about') : null;
        
        const mapCharacteristic = (c: Characteristic | undefined, cID: number) => {
            if (!c) {
                c = this.state.characteristics.find(x => x.id === cID);
            }
            if (!c) return null;

            const helpText = c.characteristicTexts ? c.characteristicTexts.find(x => x.textType === 'higH_TEXT') : null;
            const description = c.characteristicTexts ? c.characteristicTexts.find(x => x.textType === 'tooltip') : null;
            
            let charValue = 0;
            let midrangeLow = c.midStart;
            let midrangeHigh = c.midEnd;

            if (this.state.selectedUserData) {
                const charData = this.state.selectedUserData.find((x: any) => x.characteristicID === c!.id);
                if (charData) {
                    charValue = charData.average;
                    midrangeLow = this.minMaxFloor(charData.average - (charData.stdDeviation * this.stdDev));
                    midrangeHigh = this.minMaxFloor(charData.average + (charData.stdDeviation * this.stdDev));
                }
            }

            return {
                id: c.id,
                characteristicID: c.id,
                name: c.displayName || c.name,
                helpText: helpText ? helpText.value : '',
                description: description ? description.value : '',
                tooltip: description ? description.value : '',
                inclinationType: 0, // Placeholder as GroupChart uses it for step color
                value: charValue,
                midrangeLow: midrangeLow,
                midrangeHigh: midrangeHigh,
                midrangeLowOrg: midrangeLow, // We don't have separate org/all data here yet
                midrangeHighOrg: midrangeHigh,
                midrangeLowAll: midrangeLow,
                midrangeHighAll: midrangeHigh,
                teamMidRanges: []
            };
        };

        const data = group.characteristicGroupCharacteristics
            .sort(this.sortOrder)
            .map(cgc => mapCharacteristic(cgc.characteristic, cgc.characteristicID))
            .filter(x => x !== null);

        const subGroups = group.characteristicGroupSubGroups
            .sort(this.sortOrder)
            .map(sg => {
                const sgAbout = sg.characteristicGroupSubGroupTexts && sg.characteristicGroupSubGroupTexts.length > 0 ? sg.characteristicGroupSubGroupTexts[0] : null;
                return {
                    name: sg.name,
                    sort: sg.sort,
                    about: sgAbout ? sgAbout.value : '',
                    data: sg.characteristicGroupSubGroupCharacteristics
                        .map(cgsgc => mapCharacteristic(cgsgc.characteristic, cgsgc.characteristicID))
                        .filter(x => x !== null)
                };
            });

        return {
            id: group.id,
            characteristicGroupID: group.id,
            name: group.name,
            subtitle: group.subtitle,
            description: aboutText ? aboutText.value : '',
            data: data,
            comparisons: [],
            subGroups: subGroups
        };
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/team/TeamReport.tsx